import React, { FC, memo, RefObject, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, View } from 'react-native';
import { FocusableFlatList, OnDemandShortFormItemLayout, OnDemandShortFormItemTile, TWCText } from '@components';

import { assets } from '@assets';
import { Theme, VODControlsTestIDs } from '@constants';
import { helpers } from '@helpers';
import { PlaylistItem } from '@store';

import { styles } from './styles';

import { tPath } from '../constants';

type PlayListLayoutProps = {
  nextFocusUp: unknown;
  onFocus: unknown;
  onLayoutFocus: boolean;
  playlistReference: RefObject<number | null>;
  setIsPlaylistFocused: (val: boolean) => void;
  isShowingHeader?: boolean;
  playlist: PlaylistItem[];
  title: string | null;
  onPress: (_id: string) => void;
};

export const PlayListLayout: FC<PlayListLayoutProps> = memo(
  ({
    nextFocusUp,
    onFocus,
    onLayoutFocus,
    playlistReference,
    setIsPlaylistFocused,
    isShowingHeader = true,
    playlist,
    title,
    onPress,
  }) => {
    const spacing = OnDemandShortFormItemLayout.TILE_SPACING;
    const tileWidth = OnDemandShortFormItemLayout.TILE_WIDTH;

    const { t } = useTranslation('translation', { keyPrefix: tPath });

    const _getItemLayout = useCallback(
      (_: unknown, index: number) => ({
        length: tileWidth,
        offset: (tileWidth + spacing) * index,
        index,
      }),
      [tileWidth, spacing],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const _separator = useCallback(() => <View style={{ width: spacing }} />, []);

    const _keyExtractor = useCallback((_: unknown, index: number) => `playlist_item_${index + 1}`, []);

    const _renderItem = useCallback(
      (data: PlaylistItem, index: number) => {
        const safeDuration = +data?.duration ?? 0;

        const min = helpers.TimeUtils.getMinutes(safeDuration);
        const sec = helpers.TimeUtils.getSeconds(safeDuration);
        // TODO: change type after OnDemandShortFormItemTile will be refactored to ts
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tileProperties: any = {
          accessibilityLabel: t('playlistItemAccessibilityLabel', {
            index: index + 1,
            length: playlist.length,
            rowTitle: title,
            title: data.title,
            minutes: min,
            seconds: sec,
            series: data.seriestitle,
          }),
          accessibilityHint: t('playlistItemAccessibilityHint'),
          data,
          testID: `playlist_item_index_${index + 1}`,
          onPress: () => {
            onPress(data._id);
          },
        };

        return <OnDemandShortFormItemTile {...tileProperties} />;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [playlist, title],
    );

    useEffect(() => {
      setIsPlaylistFocused(!isShowingHeader);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShowingHeader]);

    if (!playlist || playlist?.length === 0) {
      return null;
    }

    return (
      <View
        style={[
          styles.playlistRailWrapper,
          {
            paddingTop: isShowingHeader ? Theme.px(0) : Theme.px(255),
          },
        ]}
      >
        {isShowingHeader ? (
          <View style={styles.infoWrapper}>
            <TWCText noTestID importantForAccessibility={'no'} style={styles.scrolldownText} variant={'mediumText24'}>
              {t('pressDownFirst')}
            </TWCText>
            <Image source={assets.arrowRightVideo} style={styles.arrowDownIcon} />
            {/* <Image source={assets.arrowRightVideo} style={styles.arrowDownIcon} /> */}
            <TWCText noTestID importantForAccessibility={'no'} style={styles.scrolldownText} variant={'mediumText24'}>
              {t('pressDownSecond')}
            </TWCText>
          </View>
        ) : (
          <TWCText noTestID importantForAccessibility={'no'} style={styles.railTitleText} variant={'mediumText36'}>
            {title}
          </TWCText>
        )}
        <FocusableFlatList
          containerStyle
          ItemSeparatorComponent={_separator}
          ListFooterComponent={View}
          ListFooterComponentStyle={{
            width: Theme.px(1920 - tileWidth),
          }}
          data={playlist}
          focusPositions={1}
          getItemLayout={_getItemLayout}
          horizontal={true}
          initialScrollIndex={0}
          itemUnderlayColor={'clear'}
          keyExtractor={_keyExtractor}
          listGainedFocus={onLayoutFocus}
          nestedScrollEnabled={true}
          nextFocusDown={null}
          nextFocusLeft={null}
          nextFocusRight={null}
          nextFocusUp={nextFocusUp}
          paddingStart={Theme.px(90)}
          ref={playlistReference}
          renderItem={_renderItem}
          shouldUseContextReference={false}
          showsHorizontalScrollIndicator={false}
          testID={VODControlsTestIDs.vodPlayList}
          wrapItem={false}
          onFocus={onFocus}
        />
      </View>
    );
  },
);
